import {Space} from 'antd';
import Dot from '../../components/Dot';
import ScaleFade from '../../components/animations/ScaleFade';
import {DEFAULT_PAGE_SIZE} from '../../constants';
import {tableHelpers} from './table.helpers';
import {colorsStyles} from '../styles';

/**
 * Collection of helper utilities for tabbed table views, including pagination, filtering, sorting, and data storage.
 * @type {Object}
 * @example
 * import { tabHelpers } from './tab.helpers';
 * tabHelpers.handleOnPaginate({ view: 'active', activePage: 2, tableData, setTableData, loadDataFunc });
 */
export const tabHelpers = {
  /**
   * Handles page change in a tabbed table view — either loads more data from the API or updates the local page state.
   * @param {Object} params - Parameters object.
   * @param {string} params.view - The active tab/view key.
   * @param {number} params.activePage - The requested page number.
   * @param {Object} params.tableData - Current table data state (single object or keyed by view).
   * @param {Function} params.setTableData - State setter for table data.
   * @param {Function} params.loadDataFunc - Function to call to fetch more data from the API.
   * @param {boolean} [params.single=false] - When true, tableData is treated as a single flat object instead of view-keyed.
   * @returns {void}
   * @example
   * tabHelpers.handleOnPaginate({ view: 'active', activePage: 3, tableData, setTableData, loadDataFunc });
   */
  handleOnPaginate: ({view, activePage, tableData, setTableData, loadDataFunc, single = false}) => {
    const viewTableData = single ? tableData : tableData[view];
    const {page: currentPage, loadedPages} = viewTableData;
    if (activePage > currentPage && loadedPages < activePage) {
      let query = {};
      if (viewTableData.pagination && viewTableData.pagination.cursor) query = {cursor: viewTableData.pagination.cursor};
      loadDataFunc({view, query});
    } else {
      setTableData(single ? {
        ...viewTableData,
        page: activePage
      } : {
        ...tableData,
        [view]: {
          ...viewTableData,
          page: activePage
        }
      });
    }
  },
  /**
   * Handles filter changes in a tabbed table view by triggering a data reload and optionally updating the stored query state.
   * @param {Object} params - Parameters object.
   * @param {string} params.view - The active tab/view key.
   * @param {Object} params.filterQuery - Query parameters representing the applied filter values.
   * @param {Function} params.loadDataFunc - Function to call to reload data with the new filter.
   * @param {Object} [params.queries] - Current queries state (keyed by view or single object).
   * @param {Function} [params.setQueries] - State setter for the queries object.
   * @param {boolean} [params.single=false] - When true, queries is treated as a single flat object instead of view-keyed.
   * @returns {void}
   * @example
   * tabHelpers.handleOnFilter({ view: 'active', filterQuery: { status: 'active' }, loadDataFunc, queries, setQueries });
   */
  handleOnFilter: ({view, filterQuery, loadDataFunc, queries = undefined, setQueries = undefined, single = false}) => {
    loadDataFunc({
      initial: true,
      view,
      query: filterQuery
    });
    if (queries && setQueries) {
      const viewQueries = single ? queries : queries[view];
      setQueries(single ? {
        ...viewQueries,
        ...filterQuery
      } : {
        ...queries,
        [view]: {
          ...viewQueries,
          ...filterQuery
        }
      });
    }
  },
  /**
   * Handles Ant Design Table onChange events; currently processes sort actions by reloading data with an order_by query.
   * @param {Object} params - Parameters object.
   * @param {string} params.view - The active tab/view key.
   * @param {Object} params.sorter - Ant Design sorter object with field and order properties.
   * @param {Object} params.extra - Ant Design extra object containing the action type string.
   * @param {Function} params.loadDataFunc - Function to call to reload data with the new sort order.
   * @returns {void}
   * @example
   * tabHelpers.handleOnTableChange({ view: 'active', sorter: { field: 'date', order: 'descend' }, extra: { action: 'sort' }, loadDataFunc });
   */
  handleOnTableChange: ({view, sorter, extra, loadDataFunc}) => {
    const {action} = extra;
    if (action === 'sort') {
      loadDataFunc({
        initial: true,
        view,
        query: {
          order_by: tableHelpers.getOrderByValue(sorter)
        }
      })
    }
  },
  /**
   * Returns a translated tab label string with a count in parentheses.
   * @param {Object} params - Parameters object.
   * @param {string} params.key - Translation key suffix used in `viewOptions.<key>`.
   * @param {Object|null} params.total - Object containing total counts keyed by view/totalKey name.
   * @param {string} [params.totalKey] - Override key for looking up the count in total; defaults to key.
   * @param {Function} params.t - i18next translation function.
   * @returns {string} Label string in the format "Tab Name (count)".
   * @example
   * tabHelpers.getLabel({ key: 'active', total: { active: 12 }, t }); // 'Active (12)'
   */
  getLabel: ({key, total, totalKey, t}) => {
    let totalPropertyName = totalKey || key;
    let value = 0;
    if (total && total.hasOwnProperty(totalPropertyName)) value = total[totalPropertyName];
    return `${t(`viewOptions.${key}`)} (${value})`;
  },
  /**
   * Returns a translated tab label, appending a dot in parentheses only when a totalKey is specified.
   * @param {Object} params - Parameters object.
   * @param {string} params.key - Translation key suffix used in `viewOptions.<key>`.
   * @param {Object|null} params.total - Object containing total counts keyed by view/totalKey name.
   * @param {string} [params.totalKey] - When provided, appends the count from total to the label; when absent the count is omitted.
   * @param {Function} params.t - i18next translation function.
   * @returns {JSX.Element} Label string, optionally suffixed with "(count)".
   * @example
   * tabHelpers.getLabelWithOptionalIndicator({ key: 'pending', total: { pending: 3 }, totalKey: 'pending', t }); // 'Pending (3)'
   */
  getLabelWithOptionalIndicator: ({key, total, totalKey, t, hideIndicator = false}) => {
    const label = t(`viewOptions.${key}`);
    const value = total?.[totalKey || key] || 0;
    const isVisibleIndicator = !hideIndicator && Boolean(value);
    return (
      <Space size='small' className='d-flex align-items-center d-flex-child'>
        <span>{label}</span>
        {isVisibleIndicator && <ScaleFade className='d-flex'><Dot color={colorsStyles.primary_5} /></ScaleFade>}
      </Space>
    );
  },
  /**
   * Computes the next table data state from an API response, handling initial load, append, and current-page update modes.
   * @param {Object} params - Parameters object.
   * @param {Object} params.tableData - Current table data for the view (with rows, page, loadedPages, pageCursors).
   * @param {Object} params.response - API response object containing rows and optional pagination cursor.
   * @param {boolean} params.initial - When true, resets the table to the first page.
   * @param {string} [params.rowsKey='results'] - Key in response that contains the data rows array.
   * @param {boolean} [params.updateCurrentPage=false] - When true, replaces only the current page's rows without advancing the page counter.
   * @param {number} [params.perPage] - Page size the response was loaded with; kept in the slice for the page-size select and the current-page offset.
   * @returns {Object} Updated table data object merged with the previous state.
   * @example
   * const updated = tabHelpers.getUpdatedTableData({ tableData, response, initial: true });
   */
  getUpdatedTableData: ({tableData, response, initial, rowsKey = 'results', updateCurrentPage = false, perPage}) => {
    const pagination = typeof response?.pagination === 'string'
      ? { cursor: response.pagination }
      : response?.pagination || null;

    const data = response[rowsKey] || [];
    const currentPage = tableData.page || 1;
    let { loadedPages, page, pageCursors = {}, rows = [] } = tableData;

    if (initial) {
      page = 1;
      loadedPages = 1;
      pageCursors = { 1: pagination?.cursor || null };
      rows = data;
    } else {
      if (!updateCurrentPage) {
        page += 1;
        loadedPages = Math.max(loadedPages, page);
        pageCursors = {
          ...pageCursors,
          [page]: pagination?.cursor || null
        }
        rows = [...rows, ...data];
      }
    }

    if (updateCurrentPage) {
      loadedPages = currentPage;
      pageCursors = Object.fromEntries(
        Object.entries(pageCursors).filter(([key]) => parseInt(key, 10) <= currentPage)
      );
      rows = currentPage <= 1 ? [] : rows.slice(0, (currentPage - 1) * (perPage || tableData.perPage || DEFAULT_PAGE_SIZE));
      rows = [...rows, ...data];
    }

    return {
      ...tableData,
      loaded: true,
      loadedPages,
      pagination,
      page,
      pageCursors,
      perPage: perPage || tableData.perPage,
      rows
    };
  },
  /**
   * Updates the table data state for a specific view by merging the API response, then calls setTableData.
   * @param {Object} params - Parameters object.
   * @param {Object} params.tableData - Current full table data state keyed by view.
   * @param {Function} params.setTableData - State setter for table data.
   * @param {string} params.view - The active tab/view key whose slice to update.
   * @param {Object} params.response - API response object containing rows and optional pagination cursor.
   * @param {boolean} params.initial - When true, resets the view to the first page.
   * @param {string} [params.rowsKey='results'] - Key in response that contains the data rows array.
   * @param {boolean} [params.updateCurrentPage=false] - When true, replaces only the current page's rows without advancing the page counter.
   * @returns {Object} The fully updated table data object (all views).
   * @example
   * const updated = tabHelpers.handleStoreTableData({ tableData, setTableData, view: 'active', response, initial: false });
   */
  handleStoreTableData: ({tableData, setTableData, view, response, initial, rowsKey = 'results', updateCurrentPage = false}) => {
    const viewTableData = tableData[view];
    let updatedViewTableDate = tabHelpers.getUpdatedTableData({
      tableData: viewTableData,
      response,
      initial,
      rowsKey,
      updateCurrentPage
    })
    let updatedTableData = {
      ...tableData,
      [view]: updatedViewTableDate
    }

    setTableData(updatedTableData);
    return updatedTableData;
  },
  /**
   * Updates the table data state for a specific view using the functional (previous state) form of setTableData to avoid stale closures.
   * @param {Object} params - Parameters object.
   * @param {Object} params.tableData - Current full table data state keyed by view (used only to access view slice).
   * @param {Function} params.setTableData - State setter for table data; called with a function receiving previous state.
   * @param {string} params.view - The active tab/view key whose slice to update.
   * @param {Object} params.response - API response object containing rows and optional pagination cursor.
   * @param {boolean} params.initial - When true, resets the view to the first page.
   * @param {string} [params.rowsKey='results'] - Key in response that contains the data rows array.
   * @param {boolean} [params.updateCurrentPage=false] - When true, replaces only the current page's rows without advancing the page counter.
   * @param {number} [params.perPage] - Page size the response was loaded with; stored in the view's slice.
   * @returns {void}
   * @example
   * tabHelpers.handleStoreTableDataWithPrev({ tableData, setTableData, view: 'active', response, initial: false });
   */
  handleStoreTableDataWithPrev: ({tableData, setTableData, view, response, initial, rowsKey = 'results', updateCurrentPage = false, perPage}) => {
    // ⚠ THE MERGE HAPPENS INSIDE THE UPDATER (H1MVP-4807). It used to read `tableData[view]` from
    // the calling scope, build the next view from it, and only then hand the finished object to
    // `setTableData(prev => ...)`. The functional form made that look safe; it protected the spread
    // and nothing else, because the rows had already been derived from a snapshot taken when the
    // request went out. Edit a cell while a load is in flight and the response reverted the edit —
    // the state was correct for a moment and then quietly went backwards.
    //
    // `tableData` is still accepted for callers that pass it, but is only a fallback for the very
    // first render; `prevState` is the authority.
    setTableData(prevState => ({
      ...prevState,
      [view]: tabHelpers.getUpdatedTableData({
        tableData: prevState?.[view] ?? tableData?.[view],
        response,
        initial,
        rowsKey,
        updateCurrentPage,
        perPage
      })
    }));
  },
  /**
   * Stores paginated table data for a specific view using a per-page slice strategy (replaces the current page's rows in the full rows array).
   * @param {Object} params - Parameters object.
   * @param {Object} params.tableData - Current full table data state keyed by view.
   * @param {Function} params.setTableData - State setter for table data.
   * @param {string} params.view - The active tab/view key whose slice to update.
   * @param {Object} params.response - API response object containing rows and optional pagination metadata.
   * @param {string} params.rowsKey - Key in response that contains the data rows array.
   * @param {number} params.perPagePagination - Number of rows per page used to compute the splice offset.
   * @returns {Object} The fully updated table data object (all views).
   * @example
   * tabHelpers.handleStoreStoredTableData({ tableData, setTableData, view: 'active', response, rowsKey: 'results', perPagePagination: 10 });
   */
  handleStoreStoredTableData: ({tableData, setTableData, view, response, rowsKey, perPagePagination}) => {
    const pagination = response?.pagination || null;
    const data = response[rowsKey] || [];
    const viewTableData = tableData[view];
    let {page} = viewTableData;
    let updatedTableData;

    updatedTableData = {
      ...tableData,
      [view]: {
        loaded: true,
        loadedPages: page,
        pagination,
        page,
        rows: page === 1 ? data : [...tableData[view].rows.slice(0, (page - 1) * perPagePagination), ...data]
      }
    }

    setTableData(updatedTableData);
    return updatedTableData;
  },
  /**
   * Builds the merged API query object for a view by combining stored queries, a new query override, and cursor pagination state.
   * @param {Object} params - Parameters object.
   * @param {boolean} params.initial - When true, sets cursor to undefined to start from the first page.
   * @param {Object} [params.query] - Additional query parameters to merge (override) on top of stored queries.
   * @param {Object} [params.queries] - Stored query state (keyed by view or single object).
   * @param {string} params.view - The active tab/view key.
   * @param {boolean} [params.updateCurrentPage] - When true, injects the cursor for the previous page from pageCursors.
   * @param {Object} [params.tableData] - Current table data state used to look up pageCursors when updateCurrentPage is true.
   * @param {boolean} [params.single=false] - When true, queries is treated as a single flat object instead of view-keyed.
   * @returns {Object} Merged query object ready to pass to an API call.
   * @example
   * const query = tabHelpers.getViewQuery({ initial: true, query: { status: 'active' }, queries, view: 'active' });
   */
  getViewQuery: ({initial, query, queries, view, updateCurrentPage, tableData, single = false}) => ({
    ...(single ? queries ?? {} : queries?.[view] ?? {}),
    ...query,
    ...(initial && { cursor: undefined }),
    ...(updateCurrentPage && tableData && tableData[view]?.page && {
      cursor: tableData[view].pageCursors[tableData[view].page - 1] || undefined
    })
  }),
  /**
   * Strips explicitly-`undefined` values out of a filter query.
   *
   * Filters clear a field by sending it as `undefined` (see TableFilters/FilterTags), not by
   * omitting the key. A page's `getQuery` typically merges the active filters over a tab's
   * mandatory default query (`...tabDefaults, ...query`), and a plain spread keeps that
   * `undefined` and overwrites whatever default came before it — so clearing a filter wiped the
   * tab's own definition instead of falling back to it (H1MVP-4882). Callers building that merge
   * should pass the filter query through this first.
   * @param {Object} query - Filter query that may contain explicit `undefined` values.
   * @returns {Object} The same query with `undefined`-valued keys removed.
   * @example
   * tabHelpers.omitUndefinedQueryValues({ sync_status: undefined, search: '' }); // { search: '' }
   */
  omitUndefinedQueryValues: (query) =>
    Object.fromEntries(Object.entries(query || {}).filter(([, value]) => value !== undefined)),
  /**
   * Derives the Ant Design table sort order descriptor from the stored queries for a given view.
   * @param {string} view - The active tab/view key.
   * @param {Object} queries - Stored query state keyed by view name.
   * @returns {{field: string, order: 'ascend'|'descend'}|undefined} Sort order object, or undefined if no sort is stored.
   * @example
   * tabHelpers.getTableOrder('active', { active: { order_by: '-date' } }); // { field: 'date', order: 'descend' }
   */
  getTableOrder: (view, queries) => {
    let tableOrder;
    const query = queries && queries.hasOwnProperty(view) ? queries[view] : undefined;
    let orderBy = query?.order_by || undefined;
    if (orderBy) {
      const desc = orderBy.charAt(0) === '-';
      if (desc) orderBy = orderBy.replace(/^./, '');
      tableOrder = {
        field: orderBy,
        order: desc ? 'descend' : 'ascend'
      }
    }
    return tableOrder;
  },
  /**
   * Builds the Ant Design Table pagination props object for a given tab view.
   * @param {Object} [params={}] - Parameters object.
   * @param {Function} params.handleOnPaginate - Callback invoked with (view, page) when the user changes page.
   * @param {Object} params.tableData - Current full table data state (keyed by view or single flat object).
   * @param {Object|null} params.total - Object containing total count values keyed by totalKey.
   * @param {string} params.totalKey - Key in total to look up the total row count.
   * @param {string} params.view - The active tab/view key.
   * @param {number} [params.perPagePagination=DEFAULT_PAGE_SIZE] - Page size used for Ant Design pagination.
   * @param {boolean} [params.single=false] - When true, tableData is treated as a single flat object instead of view-keyed.
   * @returns {{current: number, disabled: boolean, onChange: Function, pageSize: number, showSizeChanger: boolean, total: number}} Ant Design pagination props.
   * @example
   * const pagination = tabHelpers.getTablePagination({ handleOnPaginate, tableData, total, totalKey: 'active', view: 'active' });
   */
  getTablePagination: ({handleOnPaginate, tableData, total, totalKey, view, perPagePagination = DEFAULT_PAGE_SIZE, single = false} = {}) => {
    const getTableDataValue = (view, key, defaultValue) => single ? tableData?.[key] : tableData.hasOwnProperty(view) ? tableData[view][key] : defaultValue;
    const data = getTableDataValue(view, 'rows', []);
    const paginationCurrentPage = getTableDataValue(view, 'page', 1);
    const enabledPaginationCursor = Boolean(getTableDataValue(view, 'pagination', null)?.cursor);

    let paginationTotal = total && total.hasOwnProperty(totalKey) ? total[totalKey] : undefined;
    if (paginationTotal === undefined) {
      paginationTotal = data.length;
      if (enabledPaginationCursor) paginationTotal += 1; // Add 1 to total if cursor pagination is enabled
    } else if (enabledPaginationCursor) {
      paginationTotal += 1; // Add 1 to total if cursor pagination is enabled
    }

    if (!enabledPaginationCursor) paginationTotal = data.length;
    return {
      current: paginationCurrentPage,
      disabled: data.length === 0,
      onChange: (page) => handleOnPaginate(view, page),
      pageSize: perPagePagination,
      showSizeChanger: false,
      total: paginationTotal
    }
  }
}
